import { DataPointsApiResponse, definitionFromConfig, Features, getWarehousedDefinitionWithDayConfig, isBeingWarehoused, makeNoComparisonSpec, MetricConfig } from '@tableau/metrics-engine';
import { MetricDetailView, MetricRefreshTime, MetricSnapshotBadge, MetricTitle } from '@tableau/metrics-shared-widgets-dom';
import React from 'react';
import { classes, media, style } from 'typestyle';

import { Messages } from '~src/codegen/Localize';
import { FavoriteToggle } from '~src/favorites/components/favoriteToggle/FavoriteToggle';
import { metricsFeatureFlagsAdapter } from '~src/metrics';
import { IMetricInfoWithoutData } from '~src/metrics/models/IMetricInfo';
import { ActionsMenu } from '~src/spacesPlatform/components/ActionsMenu/ActionsMenu';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { ConnectedMetricDetailShareButton } from './MetricDetailShareButton';
import { MetricDetailSpaceState } from './MetricDetailSpace';
import { ConnectedMetricMetadataPanel } from './MetricMetadataPanel';
import { ConnectedMetricsBanner } from './MetricsBanner';
import { BreakPoints } from './MetricsStyles';

export interface MetricDetailSpaceContentProps {
  state: MetricDetailSpaceState;
  addFavorite: () => void;
  removeFavorite: () => void;
  metadataChanged: () => void;
  detailHolder: React.RefObject<HTMLDivElement>;
  metricDataProp: DataPointsApiResponse;
  urls: SpacesSiteUrls;
  isSpaceForWorkbookUsageMetric: boolean;
}

const emptyConfig: MetricConfig = {
    comparison: undefined,
    window: undefined,
    status: { above: null, equal: null, below: null}
};

export function MetricDetailSpaceContent({ state, addFavorite, removeFavorite, detailHolder, metricDataProp, urls, metadataChanged, isSpaceForWorkbookUsageMetric }: MetricDetailSpaceContentProps): React.ReactElement {
  const [config, setConfig] = React.useState(undefined);
  const [isEditingConfig, setIsEditingConfig] = React.useState(false);

  if (!metricDataProp) {
    return null;
  }

  const configChanged = (config: MetricConfig | undefined) => {
    if (!config) {
      setConfig(config);
      return;
    }

    if (config.comparison === null) {
      setConfig({...config, comparison: makeNoComparisonSpec()});
    } else {
      setConfig(config);
    }
  };

  const definition = getWarehousedDefinitionWithDayConfig(definitionFromConfig(config ?? emptyConfig, metricDataProp.definition));
  const detailProps = {...metricDataProp, definition, flags: metricsFeatureFlagsAdapter(), size: { height: `${state.detailHeight}px`}, disabledWindowSwitcher: isEditingConfig};
  return <div style={metricDetailSpaceContainerStyle}>
    { !isSpaceForWorkbookUsageMetric && <ConnectedMetricsBanner />}
    <div style={containerStyle} role="main">
      {renderTopRow(metricDataProp.dataRefreshedTimestamp, state.metricInfo, definition?.features, addFavorite, removeFavorite, isSpaceForWorkbookUsageMetric)}
      <div className={graphAndMetadataContainerStyle} ref={detailHolder}>
        <div className={timeSeriesContainerWithMarginStyle}>
          <MetricDetailView {...detailProps} />
        </div>
        <div className={metadataContainerStyle} role="complementary">
          <ConnectedMetricMetadataPanel
            metricInfo={state.metricInfo}
            metricDataPoints={{...metricDataProp, definition}}
            definition={metricDataProp?.definition}
            urls={urls}
            onEditingConfig={setIsEditingConfig}
            onConfigChanged={configChanged}
            metadataChanged={metadataChanged} />
        </div>
      </div>
    </div>
  </div>;
}

function renderTopRow(dataRefreshedTimestamp: Date, metricInfoWithoutData: IMetricInfoWithoutData, metricFeatures: Features, addFavorite: () => void, removeFavorite: () => void, isSpaceForWorkbookUsageMetric: boolean) {
  return (
    <div className={topRowContainerWithMarginStyle}>
      <div style={refreshTimeShareButtonRowLayoutStyle}>
        <div style={refreshTimeStyle}>
          <MetricRefreshTime lastRefreshedTimestamp={dataRefreshedTimestamp} />
          {isBeingWarehoused(metricFeatures) && (
            <div style={badgeContainerStyle}>
              <MetricSnapshotBadge tooltipText={Messages.metricsSnapshotBadgeTooltip()} />
            </div>
          )}
        </div>
        <div className={shareLayoutStyle}>
          { !isSpaceForWorkbookUsageMetric && <ConnectedMetricDetailShareButton metricInfo={metricInfoWithoutData} />}
        </div>
      </div>
      <div style={titleAndActionsContainerStyle}>
        <div style={titleLayoutStyle}>
          <MetricTitle metricName={metricInfoWithoutData.name} />
        </div>
        <div style={favoriteLayoutStyle}>
          { !isSpaceForWorkbookUsageMetric && <FavoriteToggle isFavorite={metricInfoWithoutData.favorite} onAddFavorite={addFavorite} onRemoveFavorite={removeFavorite} isTabbable />}
        </div>
        <div style={actionsLayoutStyle}>
          { !isSpaceForWorkbookUsageMetric && <ActionsMenu targetItems={[metricInfoWithoutData]} useIcon />}
        </div>
      </div>
    </div>
  );
}

const metricDetailSpaceContainerStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  paddingLeft: 'env(safe-area-inset-left)',
  paddingRight: 'env(safe-area-inset-right)',
  height: '100%'
};

const containerStyle: React.CSSProperties = {
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  height: '100%'
};

const metadataContainerStyle: string = style(
  {
    marginRight: '24px',
  },
  media({ minWidth: 0, maxWidth: BreakPoints.LARGE }, { marginLeft: '24px' }),
);

const leftMarginStyle: string = style(
  {
    marginLeft: '24px'
  },
  media({ minWidth: BreakPoints.LARGE + 1 }, { marginLeft: '56px' }),
);

const graphAndMetadataContainerStyle: string = style(
  {
    display: 'flex',
    overflow: 'auto',
    height: '100%',
  },
  media({ minWidth: 0, maxWidth: BreakPoints.LARGE }, { flexDirection: 'column' }),
  media({ minWidth: BreakPoints.LARGE + 1 }, { flexDirection: 'row' }),
);

const timeSeriesContainerStyle: string = style(
  media({ maxWidth: BreakPoints.LARGE }, { marginRight: '40px' }),
  media({ minWidth: BreakPoints.LARGE + 1 }, { flex: 4 }),
);

const topRowContainerStyle: string = style(
  {
    paddingTop: '24px',
    marginRight: '24px',
    minHeight: 'fit-content'
  }
);

const timeSeriesContainerWithMarginStyle: string = classes(leftMarginStyle, timeSeriesContainerStyle);
const topRowContainerWithMarginStyle: string = classes(leftMarginStyle, topRowContainerStyle);

const titleAndActionsContainerStyle: React.CSSProperties = {
  display: 'flex',
  minWidth: 0,
  overflow: 'hidden',
  marginTop: '10px',
  alignItems: 'center',
};
const refreshTimeShareButtonRowLayoutStyle: React.CSSProperties = {
  display: 'flex',
  minWidth: 0,
  alignItems: 'center',
};
const refreshTimeStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flex: '1 1 auto',
};
const badgeContainerStyle: React.CSSProperties = {
  marginLeft: '8px',
};
const titleLayoutStyle: React.CSSProperties = {
  minWidth: 0,
  marginRight: '24px',
  overflow: 'hidden',
};
const favoriteLayoutStyle: React.CSSProperties = {
  marginRight: '12px',
  flexShrink: 0,
};
const actionsLayoutStyle: React.CSSProperties = {
  flexShrink: 0,
};
const shareLayoutStyle: string = style(
  {
    alignSelf: 'flex-start',
    paddingLeft: '12px',
  },
  media({ maxWidth: 375 }, { display: 'none' })
);